import { useEffect, useRef } from "react";
import { axiosInstance } from "@/lib/api";
import type {
  ActiveTurn,
  ChatMessage,
  PersistedChatMessage,
} from "@/types/chat.type";
import type { StreamingBlock } from "./streamingTurnBlocks";
import { mapPersistedChatMetadata } from "./chatMessageMetadata";
import { splitTurnRounds } from "./turnRounds";

/**
 * A "streaming" row is only trusted as live for this long since its last flush.
 * MUST match the backend STALE_TURN_MS (StreamingChatTurn.ts): past it, a
 * reloaded client treats the turn as interrupted so the composer never locks
 * forever.
 */
const STALE_TURN_MS = 60_000;
/** How often to re-check an in-progress turn while resuming. */
const POLL_INTERVAL_MS = 1_200;
/** Give up resuming after this many consecutive poll failures. */
const MAX_POLL_ERRORS = 4;

export function isTurnStale(updatedAt: string, now = Date.now()): boolean {
  return now - new Date(updatedAt).getTime() > STALE_TURN_MS;
}

/** Maps a server-persisted row to the in-memory chat message shape. */
export function persistedToChatMessage(m: PersistedChatMessage): ChatMessage {
  return {
    id: m.id,
    workflowRunId: "",
    role: m.role,
    content: m.content,
    metadata: mapPersistedChatMetadata(m.metadata),
    createdAt: m.createdAt,
    toolEvents: m.toolEvents,
    status: m.status,
  };
}

/**
 * Splits a freshly loaded transcript into history to render and the assistant
 * turn the backend is still generating (if any). A "streaming" row that has
 * gone stale (server likely died mid-turn) is folded into history so its
 * partial text isn't lost and the composer doesn't stay locked.
 */
export function splitSessionMessages(rows: PersistedChatMessage[]): {
  messages: ChatMessage[];
  activeTurn: ActiveTurn | null;
} {
  const last = rows[rows.length - 1];
  if (
    last &&
    last.role === "assistant" &&
    last.status === "streaming" &&
    !isTurnStale(last.updatedAt)
  ) {
    return {
      messages: rows.slice(0, -1).map(persistedToChatMessage),
      activeTurn: last,
    };
  }
  return { messages: rows.map(persistedToChatMessage), activeTurn: null };
}

/**
 * Rebuilds render blocks from a persisted assistant row, round by round in the
 * order the turn streamed (see splitTurnRounds), so following a turn after a
 * reload looks the same as watching it live.
 */
export function buildBlocksFromMessage(
  msg: Pick<PersistedChatMessage, "content" | "metadata" | "toolEvents">,
  opts: { live: boolean },
): StreamingBlock[] {
  const thinking = msg.metadata?.thinking;
  const rounds = splitTurnRounds(
    msg.content,
    thinking && thinking.text.trim() ? thinking : null,
    msg.toolEvents ?? [],
  );
  const blocks: StreamingBlock[] = [];
  let toolIndex = 0;
  rounds.forEach((round, r) => {
    if (round.thinking) {
      blocks.push({
        kind: "thinking",
        block: {
          id: `resume-think-${r}`,
          text: round.thinking.text,
          durationMs: round.thinking.durationMs,
          isLive: false,
        },
      });
    }
    if (round.text) {
      blocks.push({
        kind: "text",
        block: {
          id: `resume-text-${r}`,
          content: round.text,
          // Only the reply still being written carries the live cursor.
          isLive: opts.live && r === rounds.length - 1,
        },
      });
    }
    for (const e of round.tools) {
      blocks.push({
        kind: "tool",
        event: {
          ...e,
          toolCallId: e.toolCallId || `resume-tool-${toolIndex}`,
          input: e.input ?? null,
          result: e.result ?? null,
          status: e.endedAt ? "completed" : "running",
        },
      });
      toolIndex += 1;
    }
  });
  return blocks;
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

type ResumeSetters = {
  setIsStreaming: (v: boolean) => void;
  setStreamingBlocks: (b: StreamingBlock[]) => void;
  setMessages: (m: ChatMessage[]) => void;
  setContextTokens?: (n: number | null) => void;
  setStreamError?: (e: string | null) => void;
  /**
   * Takes the finished transcript instead of setMessages (a paged read: the
   * caller merges the newest page over the older pages it already holds).
   */
  applyTranscript?: (data: { messages: PersistedChatMessage[] }) => void;
};

/**
 * Follows a reply the backend is still generating for this session — e.g. after
 * a page refresh mid-turn — and drives the same streaming UI a live turn uses,
 * polling until it finishes. Locks the composer (isStreaming) while resuming,
 * then reconciles from the server and unlocks.
 *
 * Keyed on the session (not on isStreaming), so a live send toggling isStreaming
 * never restarts or cancels a resume.
 *
 * `recoveryKey` lets a live send hand a BROKEN stream back to this loop: bump it
 * when the SSE connection dies before the turn's terminal event and the same
 * follow-to-completion path a reload takes runs in place, no reload needed. A
 * recovery run also reconciles when it finds no live turn (the reply may have
 * landed in the gap), which a plain mount-time resume deliberately does not.
 */
export function useResumableChatTurn(params: {
  sessionKey: string | null;
  sessionUrl: string | null;
  /** Where the finished transcript is read, when not sessionUrl (a paged read). */
  transcriptUrl?: string | null;
  isStreamingRef: { current: boolean };
  setters: ResumeSetters;
  onSettled?: () => void;
  recoveryKey?: number;
}): void {
  const {
    sessionKey,
    sessionUrl,
    transcriptUrl = null,
    isStreamingRef,
    setters,
    onSettled,
    recoveryKey = 0,
  } = params;
  const settersRef = useRef(setters);
  const onSettledRef = useRef(onSettled);
  // Last recoveryKey this effect acted on, so a session change (which re-runs
  // the effect with an unchanged key) is not mistaken for a new recovery.
  const lastRecoveryRef = useRef(recoveryKey);
  // Keep the "latest" callbacks reachable from the session-keyed resume loop
  // without making them effect deps (they change identity every render).
  useEffect(() => {
    settersRef.current = setters;
    onSettledRef.current = onSettled;
  });

  useEffect(() => {
    const isRecovery = recoveryKey !== lastRecoveryRef.current;
    lastRecoveryRef.current = recoveryKey;
    if (!sessionUrl || !sessionKey) return;
    let cancelled = false;
    const activeTurnUrl = `${sessionUrl}/active-turn`;

    const fetchActiveTurn = async (): Promise<ActiveTurn | null> => {
      const res = await axiosInstance.get<{ turn: ActiveTurn | null }>(
        activeTurnUrl,
      );
      return res.data.turn;
    };

    const reconcile = async () => {
      const s = settersRef.current;
      try {
        const res = await axiosInstance.get<{ messages: PersistedChatMessage[] }>(
          transcriptUrl ?? sessionUrl,
        );
        if (cancelled) return;
        if (s.applyTranscript) s.applyTranscript(res.data);
        else s.setMessages(res.data.messages.map(persistedToChatMessage));
        if (s.setContextTokens) {
          const lastUsage = [...res.data.messages]
            .reverse()
            .find((m) => m.metadata?.usage)?.metadata?.usage;
          s.setContextTokens(lastUsage?.contextTokens ?? null);
        }
      } catch (err) {
        if (!cancelled)
          console.error("Failed to reconcile chat after resume:", err);
      }
    };

    /** Pull the transcript, drop the streaming UI, unlock the composer. */
    const settle = async (terminalError: string | null) => {
      await reconcile();
      if (cancelled) return;
      if (terminalError) settersRef.current.setStreamError?.(terminalError);
      settersRef.current.setStreamingBlocks([]);
      settersRef.current.setIsStreaming(false);
      onSettledRef.current?.();
    };

    void (async () => {
      // Don't fight an in-flight live send in this same tab. A recovery run is
      // exempt: it is requested BY a send that has just given up, and whether
      // its isStreaming reset has landed in the same render as the recovery bump
      // is a batching detail we must not depend on.
      if (!isRecovery && isStreamingRef.current) return;

      let turn: ActiveTurn | null;
      try {
        turn = await fetchActiveTurn();
      } catch {
        // A recovery run must not leave the caller with a half-streamed turn on
        // screen just because this probe failed — fall back to the transcript.
        if (isRecovery && !cancelled) await settle(null);
        return;
      }
      if (cancelled) return;
      if (!turn || turn.status !== "streaming" || isTurnStale(turn.updatedAt)) {
        // Nothing live to follow. On recovery the turn most likely finished
        // between the socket dying and this probe, so pull the finished reply
        // in rather than leaving the send silently empty.
        if (isRecovery) {
          await settle(
            turn?.status === "error"
              ? turn.metadata?.error ?? "The assistant stopped unexpectedly."
              : null,
          );
        }
        return;
      }

      const s = settersRef.current;
      s.setIsStreaming(true);
      s.setStreamError?.(null);
      s.setStreamingBlocks(buildBlocksFromMessage(turn, { live: true }));
      if (s.setContextTokens && turn.metadata?.usage) {
        s.setContextTokens(turn.metadata.usage.contextTokens);
      }

      let terminalError: string | null = null;
      let errorCount = 0;
      while (true) {
        await delay(POLL_INTERVAL_MS);
        if (cancelled) return;
        try {
          turn = await fetchActiveTurn();
          errorCount = 0;
        } catch {
          if (++errorCount >= MAX_POLL_ERRORS) break;
          continue;
        }
        if (cancelled) return;
        if (turn && turn.status === "streaming" && !isTurnStale(turn.updatedAt)) {
          settersRef.current.setStreamingBlocks(
            buildBlocksFromMessage(turn, { live: true }),
          );
          if (settersRef.current.setContextTokens && turn.metadata?.usage) {
            settersRef.current.setContextTokens(turn.metadata.usage.contextTokens);
          }
          continue;
        }
        if (turn && turn.status === "error") {
          terminalError =
            turn.metadata?.error ?? "The assistant stopped unexpectedly.";
        }
        // "aborted" is terminal too, and deliberately sets no terminalError:
        // the user pressed Stop, so there is nothing to report to them. It
        // settles through the same path a completed turn does, which pulls the
        // partial reply into the transcript marked "Stopped".
        break;
      }

      if (cancelled) return;
      await settle(terminalError);
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionKey, sessionUrl, transcriptUrl, isStreamingRef, recoveryKey]);
}
